class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.addEventListener('submit', (event) => {
      if (event.target.matches('[data-cart-discount-form]')) this.applyDiscount(event);
    });
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-cart-discount-remove]')) this.removeDiscount(event);
    });
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    if (typeof parsedState.item_count === 'number') {
      this.classList.toggle('is-empty', parsedState.item_count === 0);
    }

    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement || !parsedState.sections?.[section.id]) return;
      const sectionContents = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      if (sectionContents === null) return;
      sectionElement.innerHTML = sectionContents;
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML ?? null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  async applyDiscount(event) {
    event.preventDefault();

    const form = event.target;
    const input = form.querySelector('[name="discount"]');
    const button = form.querySelector('button[type="submit"]');
    const status = form.parentElement.querySelector('[data-cart-discount-status]');
    const code = input.value.trim();

    if (!code) {
      this.setDiscountStatus(status, form.dataset.emptyMessage, 'error');
      input.focus();
      return;
    }

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.querySelector('.loading__spinner')?.classList.remove('hidden');
    this.setDiscountStatus(status, '', '');

    const body = JSON.stringify({
      discount: code,
      sections: this.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname,
    });

    try {
      const response = await fetch(routes.cart_update_url, { ...fetchConfig(), body });
      const state = await response.json();

      if (!response.ok) throw new Error(state.description || state.message);

      const discountApplied = this.hasAppliedDiscount(state, code);
      this.renderContents(state);

      requestAnimationFrame(() => {
        const updatedForm = this.querySelector('[data-cart-discount-form]');
        const updatedInput = updatedForm?.querySelector('[name="discount"]');
        const updatedStatus = this.querySelector('[data-cart-discount-status]');

        if (updatedInput) updatedInput.value = discountApplied ? '' : code;
        this.setDiscountStatus(
          updatedStatus,
          discountApplied ? form.dataset.successMessage : form.dataset.errorMessage,
          discountApplied ? 'success' : 'error'
        );
        if (!discountApplied) updatedInput?.focus();
      });

    } catch (error) {
      this.setDiscountStatus(status, form.dataset.errorMessage, 'error');
      console.error(error);
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.querySelector('.loading__spinner')?.classList.add('hidden');
    }
  }

  async removeDiscount(event) {
    event.preventDefault();

    const button = event.target.closest('[data-cart-discount-remove]');
    const form = this.querySelector('[data-cart-discount-form]');
    const status = this.querySelector('[data-cart-discount-status]');
    if (!button || !form) return;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    this.setDiscountStatus(status, '', '');

    const body = JSON.stringify({
      discount: '',
      sections: this.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname,
    });

    try {
      const response = await fetch(routes.cart_update_url, { ...fetchConfig(), body });
      const state = await response.json();

      if (!response.ok) throw new Error(state.description || state.message);

      this.renderContents(state);

      requestAnimationFrame(() => {
        const updatedForm = this.querySelector('[data-cart-discount-form]');
        const updatedStatus = this.querySelector('[data-cart-discount-status]');
        this.setDiscountStatus(updatedStatus, updatedForm?.dataset.removedMessage, 'success');
        updatedForm?.querySelector('[name="discount"]')?.focus();
      });
    } catch (error) {
      this.setDiscountStatus(status, form.dataset.errorMessage, 'error');
      console.error(error);
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }

  hasAppliedDiscount(cart, submittedCode) {
    const normalizedCode = submittedCode.trim().toLowerCase();
    const discountCodes = Array.isArray(cart.discount_codes) ? cart.discount_codes : [];
    const matchingCode = discountCodes.find((discount) => discount.code?.trim().toLowerCase() === normalizedCode);

    if (matchingCode) return matchingCode.applicable !== false;

    const applications = [
      ...(cart.discount_applications || []),
      ...(cart.cart_level_discount_applications || []),
    ];

    (cart.items || []).forEach((item) => {
      applications.push(...(item.discounts || []));
      (item.line_level_discount_allocations || []).forEach((allocation) => {
        if (allocation.discount_application) applications.push(allocation.discount_application);
      });
    });

    return applications.some((application) => {
      const identifier =
        application.code ||
        application.title ||
        application.discount_application?.code ||
        application.discount_application?.title;
      return identifier?.trim().toLowerCase() === normalizedCode;
    });
  }

  setDiscountStatus(element, message, status) {
    if (!element) return;
    element.textContent = message || '';
    if (status) {
      element.dataset.status = status;
    } else {
      delete element.dataset.status;
    }
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
